import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { Award } from 'lucide-react';
import AuthorsLink from '../../Views/Helpers/AuthorsLink';
import { resolvePublicationAuthors } from '../../../helpers/resolvePublicationAuthors';
import { activePublicationLinks } from '../../../helpers/publicationLinks';
import { vocabTitle } from '../../../helpers/content';
import '../../Views/academicons/css/academicons.min.css';

const getAuthorName = (author) =>
  author?.publication_name || author?.full_name || author?.title || '';

/**
 * Columns as data rather than JSX, so a generic table variation for other
 * content types is a matter of handing this component a different column
 * list instead of writing another table.
 *
 * `className` ends up on both the <th> and the <td>, which is where the
 * per-column width and alignment live.
 */
const COLUMNS = [
  {
    id: 'year',
    label: 'Year',
    className: 'listing-table__col--year',
    render: (item) => vocabTitle(item.publication_year) || '',
  },
  {
    id: 'authors',
    label: 'Authors',
    className: 'listing-table__col--authors',
    render: (item, { authors, showAuthorLinks }) =>
      authors.length === 0 ? null : showAuthorLinks ? (
        <AuthorsLink authors={authors} inline />
      ) : (
        authors.map(getAuthorName).filter(Boolean).join(', ')
      ),
  },
  {
    id: 'title',
    label: 'Title',
    className: 'listing-table__col--title',
    render: (item, { hideLink }) => {
      const title = item.title || item.id;
      return (
        <>
          {hideLink ? (
            title
          ) : (
            <UniversalLink href={item['@id']}>{title}</UniversalLink>
          )}
          {item.is_open_access && (
            <span
              className="listing-table__badge"
              title="Open Access"
              aria-label="Open Access"
            >
              <i className="ai ai-open-access" aria-hidden="true" />
            </span>
          )}
          {item.is_best_paper && (
            <span
              className="listing-table__badge"
              title="Best Paper Award"
              aria-label="Best Paper Award"
            >
              <Award size={14} aria-hidden="true" />
            </span>
          )}
        </>
      );
    },
  },
  {
    id: 'venue',
    label: 'Published in',
    className: 'listing-table__col--venue',
    optional: true,
    render: (item) => item.publication_published_in ?? '',
  },
  {
    id: 'links',
    label: 'Links',
    className: 'listing-table__col--links',
    render: (item, { links }) =>
      links.map(({ key, label, href }) => (
        <a
          key={key}
          href={href}
          className="listing-table__link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </a>
      )),
  },
];

const PublicationTableRow = memo(
  ({ item, columns, showAuthorLinks, hideLink }) => {
    const authors = useMemo(() => resolvePublicationAuthors(item), [item]);
    const links = useMemo(() => activePublicationLinks(item), [item]);
    const ctx = { authors, links, showAuthorLinks, hideLink };
    return (
      <tr>
        {columns.map((col) => (
          <td key={col.id} className={col.className}>
            {col.render(item, ctx)}
          </td>
        ))}
      </tr>
    );
  },
);
PublicationTableRow.displayName = 'PublicationTableRow';

/**
 * Compact, striped table listing for publications - the long lists under
 * /publications and on the topic/project sub-pages are quicker to scan
 * as rows than as cards.
 *
 * The table sits in its own horizontally scrollable wrapper: below 768px
 * the card layouts stack, but a five-column table cannot, so it scrolls
 * inside the page instead of widening it.
 */
const PublicationTableListingView = ({
  items,
  show_author_links,
  hide_link,
  show_venue,
}) => {
  const columns = COLUMNS.filter(
    (col) => !col.optional || col.id !== 'venue' || show_venue !== false,
  );

  return (
    <div className="view-wrapper">
      <div className="listing-table__scroll">
        <table className="listing-table listing-table--striped">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.id} scope="col" className={col.className}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items &&
              items.map((item) => (
                <PublicationTableRow
                  key={item['@id']}
                  item={item}
                  columns={columns}
                  showAuthorLinks={show_author_links !== false}
                  hideLink={hide_link}
                />
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

PublicationTableListingView.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  show_author_links: PropTypes.bool,
  hide_link: PropTypes.bool,
  show_venue: PropTypes.bool,
};

export default PublicationTableListingView;
